import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, ArrowLeft, AlertTriangle } from 'lucide-react';
import { loadRecaptcha } from '@/lib/recaptcha';

export function Field({ label, error, children, htmlFor, hint }) {
  return (
    <div>
      {label && (
        <label htmlFor={htmlFor} className="field-label">
          {label}
        </label>
      )}
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-navy/65">{hint}</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function Honeypot({ value, onChange }) {
  // Every lead form renders this field, so it is also where the second layer
  // of bot protection starts: reCAPTCHA v3 loads once a form is on screen,
  // which gives it time to observe the page before a token is requested.
  useEffect(() => {
    loadRecaptcha();
  }, []);
  return (
    <div className="absolute -left-[9999px] top-0" aria-hidden="true">
      <label>
        Leave this field empty
        <input type="text" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
    </div>
  );
}

export function Consent({ checked, onChange, error, testid = 'form-consent-checkbox' }) {
  return (
    <div>
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid}
          className="mt-1 h-4 w-4 shrink-0 rounded-sm border-navy/30 accent-[#2E4C63]"
        />
        <span className="text-sm leading-relaxed text-navy/70">
          I agree to be contacted by HyenaMind about my request and understand my information will be handled as described in
          the privacy policy.
        </span>
      </label>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <RecaptchaNotice className="mt-3" />
    </div>
  );
}

// Google permits hiding the floating reCAPTCHA badge (it collides with the
// mobile sticky CTA) only if this disclosure is visible in the form instead.
export function RecaptchaNotice({ className = '' }) {
  const link = 'underline underline-offset-2 hover:text-navy transition-colors';
  return (
    <p className={`text-xs leading-relaxed text-navy/65 ${className}`} data-testid="recaptcha-notice">
      This site is protected by reCAPTCHA and the Google{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className={link}>
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className={link}>
        Terms of Service
      </a>{' '}
      apply.
    </p>
  );
}

export function ChoiceCards({ options, value, onChange, columns = 2, name }) {
  return (
    <div className={`grid gap-3 ${columns === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`} role="radiogroup" aria-label={name}>
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            type="button"
            key={opt}
            role="radio"
            aria-checked={selected}
            data-testid={`choice-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
            onClick={() => onChange(opt)}
            className={`relative px-4 py-3.5 rounded-md border text-left text-sm font-bold transition-all duration-200 ${
              selected ? 'border-steel bg-steel/5 text-steel ring-2 ring-steel/25' : 'border-navy/15 text-navy/75 hover:border-navy/40 bg-white'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function Chips({ options, values, onChange, name }) {
  const toggle = (opt) => onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
  return (
    <div className="flex flex-wrap gap-2" aria-label={name}>
      {options.map((opt) => {
        const selected = values.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            aria-pressed={selected}
            data-testid={`chip-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
            onClick={() => toggle(opt)}
            className={`px-4 py-2 rounded-sm border text-xs font-bold tracking-wide transition-all duration-200 ${
              selected ? 'border-steel bg-steel text-bone' : 'border-navy/20 text-navy/70 hover:border-navy/50 bg-white'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function FormNav({ onBack, backLabel = 'Back', nextLabel = 'Continue', loading = false, isLast = false, canBack = true }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-4">
      {canBack ? (
        <button
          type="button"
          onClick={onBack}
          data-testid="form-back-button"
          className="inline-flex items-center gap-2 text-sm font-bold text-navy/60 hover:text-navy transition-colors py-3"
        >
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </button>
      ) : (
        <span />
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid="form-next-button"
        className="btn-primary min-w-[10rem] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {isLast ? 'Submit Request' : nextLabel}
        {!loading && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function FormError({ message, onRetry }) {
  return (
    <div className="mt-6 flex items-start gap-3 rounded-md border border-red-700/30 bg-red-50 p-4" role="alert" data-testid="form-error-alert">
      <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-bold text-red-800">Something went wrong</p>
        <p className="mt-1 text-sm text-red-700">{message}</p>
        {onRetry && (
          <button type="button" onClick={onRetry} data-testid="form-retry-button" className="mt-2 text-sm font-bold text-red-800 underline">
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

export function SuccessPanel({ title, referenceId, steps, resourceLink, dark = false, testid = 'form-success-panel' }) {
  return (
    <div data-testid={testid} className={`rounded-md border p-8 sm:p-12 ${dark ? 'border-white/15 bg-white/[0.03]' : 'border-navy/10 bg-white'}`}>
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-forest/10">
        <CheckCircle2 className="h-7 w-7 text-forest" />
      </span>
      <h2 className={`mt-6 font-display text-3xl sm:text-4xl font-medium tracking-tight ${dark ? 'text-bone' : 'text-navy'}`}>{title}</h2>
      {referenceId && (
        <p className={`mt-3 text-sm ${dark ? 'text-silver' : 'text-navy/55'}`}>
          Reference: <span className="font-mono font-bold">{referenceId.slice(0, 8).toUpperCase()}</span>
        </p>
      )}
      <div className="mt-8">
        <h3 className={`text-xs font-bold uppercase tracking-[0.2em] ${dark ? 'text-steel-light' : 'text-steel'}`}>What happens next</h3>
        <ol className="mt-4 flex flex-col gap-3">
          {steps.map((s, i) => (
            <li key={i} className={`flex gap-3 text-base leading-relaxed ${dark ? 'text-silver' : 'text-navy/70'}`}>
              <span className={`font-display font-semibold ${dark ? 'text-steel-light' : 'text-steel'}`}>{i + 1}.</span>
              {s}
            </li>
          ))}
        </ol>
      </div>
      {resourceLink && (
        <Link
          to={resourceLink.to}
          data-testid="form-success-resource-link"
          className={`group mt-8 inline-flex items-center gap-2.5 text-base font-bold border-b-2 pb-1 transition-colors ${
            dark
              ? 'text-steel-light border-steel-light/30 hover:text-bone hover:border-bone/50'
              : 'text-navy border-navy/20 hover:text-steel hover:border-steel'
          }`}
        >
          {resourceLink.label}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      )}
    </div>
  );
}
